import Decimal from 'decimal.js'

import {
    ProductPriceModifierType,
    ProductsProductPriceModifierBillingTypeChoices,
    QuotedPaymentMethodType,
    QuotedProductType,
    QuoteFragment,
} from 'generated/graphql'
import * as settings from 'settings'

export type ProductCategoryAggregate = {
    name: string
    total: number
    totalDealer: number
    quantity: number
}

// Helpers
const applyMultiplier = (value: number, multiplier: number, enableDiscount: boolean): number => {
    if (!enableDiscount && multiplier < 1) return value
    return multiplier > 0 ? value * multiplier : value
}

export const isBundleLine = (product: {bundleName?: string | null}): boolean => Boolean(product.bundleName)

export const bundleMultiplierOf = (product: {
    bundleName?: string | null
    bundleMultiplier?: number | string | null
}): number => {
    if (!isBundleLine(product) || product.bundleMultiplier == null || product.bundleMultiplier === '') return 1
    const multiplier = parseFloat(product.bundleMultiplier.toString())
    return Number.isFinite(multiplier) ? multiplier : 1
}

export const extendedQuantityOf = (product: {
    quantity: number
    bundleName?: string | null
    bundleQuantity?: number | null
}): number => (isBundleLine(product) ? product.quantity * (product.bundleQuantity || 1) : product.quantity)

/** List × extended quantity, without the bundle multiplier. */
export const listExtendedAmount = (product: {
    price: number | string
    quantity: number
    bundleName?: string | null
    bundleQuantity?: number | null
}): number => parseFloat(product.price.toString()) * extendedQuantityOf(product)

/** List × extended quantity × bundle multiplier. Kit and payment method stay outside. */
export const farmerLineAmount = (product: {
    price: number | string
    quantity: number
    bundleName?: string | null
    bundleMultiplier?: number | string | null
    bundleQuantity?: number | null
}): number => parseFloat(product.price.toString()) * extendedQuantityOf(product) * bundleMultiplierOf(product)

export const bundleMultiplierPercent = (multiplier: number | string): number =>
    (1 - parseFloat(multiplier.toString())) * 100

export const formatBundleMultiplierPercent = (multiplier: number | string): string =>
    new Intl.NumberFormat(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 0}).format(
        bundleMultiplierPercent(multiplier),
    )

export const calculateTax = (value: number, tax: Decimal = new Decimal('0.21')): number => {
    return tax.mul(value).toNumber()
}

// Price Modifier
export const getPriceModifierForProduct = (
    priceModifiers:
        | Pick<
              ProductPriceModifierType,
              'id' | 'enableDiscount' | 'billingType' | 'commissionMultiplier' | 'productExternalIds'
          >[]
        | undefined,
    externalId: string,
    billingType: ProductsProductPriceModifierBillingTypeChoices,
): Pick<ProductPriceModifierType, 'id' | 'enableDiscount' | 'billingType' | 'commissionMultiplier'> => {
    const priceModifier = priceModifiers?.find(
        pm => pm.billingType === billingType && pm.productExternalIds?.includes(externalId),
    )
    if (!priceModifier) return createDefaultPriceModifierPayload(billingType)
    return priceModifier
}

export const createDefaultPriceModifierPayload = (
    billingType: ProductsProductPriceModifierBillingTypeChoices,
): Pick<ProductPriceModifierType, 'id' | 'enableDiscount' | 'billingType' | 'commissionMultiplier'> => {
    return {
        id: '',
        enableDiscount: false,
        billingType,
        commissionMultiplier: '1',
    }
}

// Unit

export const calculateProductUnitPrice = (price: number | string): number => {
    return Number(parseFloat(price.toString()).toFixed(Number(settings.MAX_DECIMALS)))
}

export const calculateProductUnitPriceForDealer = (
    price: number | string,
    commissionMultiplier: number | string,
    surchargeMultiplier: number = 1,
): number => {
    let result = parseFloat(price.toString()) * parseFloat(commissionMultiplier.toString()) * surchargeMultiplier
    return Number(result.toFixed(Number(settings.MAX_DECIMALS)))
}

// Total
export const calculateProductTotalPrice = (price: number, quantity: number): number => {
    return price * quantity
}

export const calculateProductTotalPriceForDealer = (
    price: number,
    commissionMultiplier: number | string,
    quantity: number,
    taxes?: {name: string; amount: number}[],
): number => {
    return calculateProductUnitPriceForDealer(
        price * quantity + (taxes ? taxes.reduce((total, tax) => total + tax.amount, 0) : 0),
        commissionMultiplier,
    )
}

/** Farmer line amount × product commission. Bundle multiplier is already in the farmer amount. */
export const dealerLineAmount = (product: {
    price: number | string
    quantity: number
    bundleName?: string | null
    bundleMultiplier?: number | string | null
    bundleQuantity?: number | null
    commissionMultiplier?: number | string | null
}): number => calculateProductTotalPriceForDealer(farmerLineAmount(product), product.commissionMultiplier ?? 1, 1)

/**
 * How much lower the dealer unit is vs farmer unit: (Farmer − Dealer) / Farmer × 100.
 * E.g. dealer 46.4 and farmer 58 → 20% (dealer is 20% less than farmer).
 * `effectiveListUnitPrice` is the list price per unit (include negotiated sale multiplier if any).
 */
export const calculateProductUnitMarginPercent = (
    effectiveListUnitPrice: number,
    commissionMultiplier: number | string | undefined,
    surchargeMultiplier: number = 1,
): number | null => {
    const farmerUnit = calculateProductUnitPrice(effectiveListUnitPrice)
    const dealerUnit = calculateProductUnitPriceForDealer(
        effectiveListUnitPrice,
        commissionMultiplier ?? '1',
        surchargeMultiplier,
    )
    if (!farmerUnit || !Number.isFinite(farmerUnit)) return null
    return ((farmerUnit - dealerUnit) / farmerUnit) * 100
}

export const formatMarginPercent = (percent: number | null | undefined): string => {
    if (percent === null || percent === undefined || !Number.isFinite(percent)) return '—'
    return `${new Intl.NumberFormat(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 0}).format(percent)} %`
}

// List
export const calculateProductsTotalPrice = (
    products: Pick<QuotedProductType, 'price' | 'quantity'>[],
): number => {
    return products.reduce((total, product) => total + farmerLineAmount(product), 0)
}

export const calculateProductsTotalPriceNegotiated = (
    products: Pick<QuotedProductType, 'price' | 'externalId' | 'quantity'>[],
    multiplier: number | undefined,
): number => {
    return products.reduce(
        (total, product) =>
            total + calculateProductTotalPrice(parseFloat(product.price) * (multiplier || 1), product.quantity),
        0,
    )
}

export const calculateProductsTotalPriceNegotiatedForDealer = (
    products: Pick<QuotedProductType, 'price' | 'commissionMultiplier' | 'quantity'>[],
    multiplier?: number,
): number => {
    return products.reduce(
        (total, product) =>
            total +
            calculateProductTotalPriceForDealer(
                parseFloat(product.price) * (multiplier || 1),
                parseFloat(product.commissionMultiplier),
                product.quantity,
            ),
        0,
    )
}

export const calculateProductsTotalPriceForDealer = (
    products: Pick<QuotedProductType, 'price' | 'commissionMultiplier' | 'quantity'>[],
): number => {
    return products.reduce(
        (total, product) =>
            total +
            calculateProductTotalPriceForDealer(
                farmerLineAmount(product),
                parseFloat(product.commissionMultiplier),
                1,
            ),
        0,
    )
}

export const calculateProductsTotalPriceByCategory = (
    products: Pick<QuotedProductType, 'price' | 'quantity'>[],
): number => {
    return products.reduce(
        (total, product) => total + calculateProductTotalPrice(parseFloat(product.price), product.quantity),
        0,
    )
}

export const calculateProductsTotalPriceForDealerByCategory = (
    products: Pick<QuotedProductType, 'price' | 'commissionMultiplier' | 'quantity'>[],
): number => {
    return products.reduce(
        (total, product) =>
            total +
            calculateProductTotalPriceForDealer(
                parseFloat(product.price),
                parseFloat(product.commissionMultiplier),
                product.quantity,
            ),
        0,
    )
}

export const calculateCategoriesTotalPrice = (categories: ProductCategoryAggregate[]): number => {
    return categories.reduce((total, category) => total + category.total, 0)
}

export const calculateCategoriesTotalPriceForDealer = (categories: ProductCategoryAggregate[]): number => {
    return categories.reduce((total, category) => total + category.totalDealer, 0)
}

export const calculateProductQuantities = (products: Pick<QuotedProductType, 'quantity'>[]): number => {
    return products.reduce((total, product) => total + product.quantity, 0)
}

export const calculateTotalTaxes = (
    products: Pick<QuotedProductType, 'quantity' | 'price'>[],
    multiplier?: number,
    tax?: Decimal,
): number => {
    return products.reduce((total, product) => {
        return total + calculateTax(farmerLineAmount(product) * (multiplier || 1), tax)
    }, 0)
}

export const calculateTotalTaxesForPaymentMethod = (
    products: Pick<QuotedProductType, 'quantity' | 'price'>[],
    tax?: Decimal,
): number => {
    return products.reduce((total, product) => {
        return total + product.quantity * calculateTax(parseFloat(product.price), tax)
    }, 0)
}

export const getMultiplierToApply = (
    multiplier: string,
    extraMultiplier: string,
    kitMultiplier: string,
    commissionMultiplier: string,
    dealer?: boolean,
) => {
    return parseFloat(multiplier) >= 1
        ? (dealer ? parseFloat(commissionMultiplier) : 1) *
              parseFloat(multiplier) *
              (1 - (1 - parseFloat(extraMultiplier)) - (1 - parseFloat(kitMultiplier)))
        : (dealer ? parseFloat(commissionMultiplier) : 1) *
              (parseFloat(multiplier) - (1 - parseFloat(extraMultiplier)) - (1 - parseFloat(kitMultiplier)))
}

export const calculateMultiplierToNegotiatedPrice = (
    quote: Pick<QuoteFragment, 'sale' | 'kitMultiplier'> | undefined,
    paymentMethodSelected?: Pick<QuotedPaymentMethodType, 'multiplier' | 'extraMultiplier' | 'dealerCommission'>,
) => {
    //TODO
    const paymentMethod = quote?.sale ? quote?.sale.paymentMethod : paymentMethodSelected
    return paymentMethod
        ? getMultiplierToApply(
              paymentMethod.multiplier,
              paymentMethod.extraMultiplier,
              quote?.kitMultiplier?.multiplier || '1',
              paymentMethod.dealerCommission || '1',
          )
        : 1
}

type BundlePricedProduct = {
    bundleName?: string | null
    bundleMultiplier?: number | string | null
    bundleQuantity?: number | null
}

export const calculatePaymentMethodTotalPrice = (
    products: (Pick<QuotedProductType, 'enableDiscount' | 'price' | 'commissionMultiplier' | 'quantity'> &
        BundlePricedProduct)[],
    paymentMethod: Pick<QuotedPaymentMethodType, 'multiplier' | 'extraMultiplier' | 'finnancied'>,
    kitMultiplier: number,
    dealer?: boolean,
    surchargeMultiplier: number = 1,
): number => {
    return products.reduce((total, product) => {
        const {enableDiscount, commissionMultiplier} = {
            ...product,
            commissionMultiplier: parseFloat(product.commissionMultiplier),
        }
        const priceOfProduct = parseFloat(product.price) * bundleMultiplierOf(product)
        const {multiplier, extraMultiplier, finnancied} = {
            ...paymentMethod,
            multiplier: parseFloat(paymentMethod.multiplier),
            extraMultiplier: parseFloat(paymentMethod.extraMultiplier),
        }
        const chargeQuantity = extendedQuantityOf(product)

        let productPrice = dealer
            ? priceOfProduct +
              (commissionMultiplier > 1
                  ? priceOfProduct * (commissionMultiplier - 1)
                  : -priceOfProduct * (1 - commissionMultiplier))
            : priceOfProduct

        let price = [
            finnancied ? 1 : multiplier,
            extraMultiplier,
            (typeof kitMultiplier === 'string' && parseFloat(kitMultiplier)) || kitMultiplier,
        ].reduce((acc, priceMultiplier) => {
            if (!enableDiscount) return acc
            if (priceMultiplier > 1) return acc + productPrice * (priceMultiplier - 1)
            return acc - productPrice * (1 - priceMultiplier)
        }, productPrice)

        if (finnancied) {
            if (multiplier > 1) price = price + price * (multiplier - 1)
            else price = price - price * (1 - multiplier)
        }
        price = calculateProductTotalPrice(price, chargeQuantity)
        return total + price
    }, 0) * surchargeMultiplier
}

export const calculatePaymentMethodTotalPriceDisplay = (
    products: (Pick<QuotedProductType, 'quantity' | 'commissionMultiplier' | 'price' | 'enableDiscount'> &
        BundlePricedProduct)[],
    paymentMethod: Pick<QuotedPaymentMethodType, 'extraMultiplier' | 'multiplier' | 'dealerCommission'>,
    kitMultiplier: number,
    surchargeMultiplier: number = 1,
): number => {
    return products.reduce((total, product) => {
        const {enableDiscount} = product
        const {multiplier, extraMultiplier} = paymentMethod

        const multiplierToApply = getMultiplierToApply(
            multiplier,
            extraMultiplier,
            kitMultiplier.toString(),
            product.commissionMultiplier,
        )

        const base = parseFloat(product.price) * bundleMultiplierOf(product)
        return total + applyMultiplier(base, multiplierToApply, enableDiscount) * extendedQuantityOf(product)
    }, 0) * surchargeMultiplier
}

export const calculatePaymentMethodTotalPriceWithTax = (
    ...args: Parameters<typeof calculatePaymentMethodTotalPrice>
): number => {
    return calculatePaymentMethodTotalPrice(...args) * 1.21
}

export const calculatePaymentMethodTotalPriceWithTaxDisplay = (
    ...args: Parameters<typeof calculatePaymentMethodTotalPriceDisplay>
): number => {
    return calculatePaymentMethodTotalPriceDisplay(...args) * 1.21
}

export const calculateDealerCommissionPaymentMethod = (
    products: Parameters<typeof calculatePaymentMethodTotalPriceDisplay>[0],
    paymentMethod: Parameters<typeof calculatePaymentMethodTotalPriceDisplay>[1],
    kitMultiplier: Parameters<typeof calculatePaymentMethodTotalPriceDisplay>[2],
    dealerCommissionFromGroup?: number,
): number => {
    return (
        calculatePaymentMethodTotalPriceWithTaxDisplay(products, paymentMethod, kitMultiplier) *
        0.87 *
        ((dealerCommissionFromGroup ? +dealerCommissionFromGroup : parseFloat(paymentMethod.dealerCommission || '0')) /
            100) *
        ((dealerCommissionFromGroup ? +dealerCommissionFromGroup : parseFloat(paymentMethod.dealerCommission || '0')) /
            100)
    )
}

export const calculateCommissionOnTotalForPPPaymentMethod = (
    products: Parameters<typeof calculatePaymentMethodTotalPriceDisplay>[0],
    paymentMethod: Parameters<typeof calculatePaymentMethodTotalPriceDisplay>[1],
    kitMultiplier: Parameters<typeof calculatePaymentMethodTotalPriceDisplay>[2],
    dealerCommissionFromGroup?: number,
): string => {
    return (
        (calculateDealerCommissionPaymentMethod(products, paymentMethod, kitMultiplier, dealerCommissionFromGroup) *
            100) /
        calculatePaymentMethodTotalPriceWithTaxDisplay(products, paymentMethod, kitMultiplier)
    ).toFixed(2)
}
